import { useCallback, useRef, useState } from 'react';
import { uploadStaffDocument, MAX_STAFF_DOC_BYTES } from '../api/HRAPI/staffRegistrationAPI';

// Staff documents are uploaded to S3 the moment they are picked (POST /HR/UploadStaffDocument), so the
// user sees progress and a confirmed result per file before saving. The form then sends only the
// returned Path of each 'done' upload. Used by StaffJoinRegistration and VerifyStaffRegistration.
//
// uploads: { [docName]: { file, status: 'uploading' | 'done' | 'error', percent, path, url, fileType, error, retryable } }

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

// required at registration (PersonalUndertaking is asked for on the compliance step)
export const STAFF_MANDATORY_DOCS = ['Photo', 'Aadhar Card', 'BankDetails', 'BackgroundVerification', 'PersonalUndertaking'];

export const validateStaffDocFile = (file) => {
    if (!file) return 'No file selected';
    if (file.size > MAX_STAFF_DOC_BYTES) return `${file.name} is larger than 5 MB`;
    if (!ALLOWED_TYPES.includes(file.type) && !/\.(pdf|jpe?g|png)$/i.test(file.name || '')) {
        return 'Only PDF, JPG or PNG files can be uploaded';
    }
    return null;
};

export default function useStaffDocUploads(empRefNo) {
    const [uploads, setUploads] = useState({});
    // a newer pick / remove of the same document makes older upload results stale
    const tokens = useRef({});

    const patch = (docName, values) =>
        setUploads(prev => (prev[docName] ? { ...prev, [docName]: { ...prev[docName], ...values } } : prev));

    const start = useCallback(async (docName, file) => {
        const token = (tokens.current[docName] || 0) + 1;
        tokens.current[docName] = token;
        const invalid = validateStaffDocFile(file);
        if (invalid) {
            setUploads(prev => ({ ...prev, [docName]: { file, status: 'error', percent: 0, error: invalid, retryable: false } }));
            return false;
        }
        setUploads(prev => ({ ...prev, [docName]: { file, status: 'uploading', percent: 0 } }));
        try {
            const data = await uploadStaffDocument(file, docName, empRefNo, (pct) => {
                // 100% sent is not done yet: the server still stores and checks the file
                if (tokens.current[docName] === token) patch(docName, { percent: Math.min(pct, 99) });
            });
            if (tokens.current[docName] !== token) return false;
            patch(docName, { status: 'done', percent: 100, path: data.Path, url: data.DownloadURL, fileType: data.FileType, error: null });
            return true;
        } catch (e) {
            if (tokens.current[docName] !== token) return false;
            patch(docName, { status: 'error', error: e.message || 'Upload failed', retryable: true });
            return false;
        }
    }, [empRefNo]);

    const retry = useCallback((docName) => {
        const file = uploads[docName]?.file;
        if (file) start(docName, file);
    }, [uploads, start]);

    const remove = useCallback((docName) => {
        tokens.current[docName] = (tokens.current[docName] || 0) + 1;
        setUploads(prev => { const next = { ...prev }; delete next[docName]; return next; });
    }, []);

    const reset = useCallback(() => {
        Object.keys(tokens.current).forEach(k => { tokens.current[k] += 1; });
        setUploads({});
    }, []);

    const names = (status) => Object.keys(uploads).filter(k => uploads[k].status === status);
    const summary = { uploading: names('uploading'), failed: names('error'), done: names('done') };

    // documents ready to save: { docName, path, fileType }
    const completedDocs = () => summary.done.map(docName => ({
        docName, path: uploads[docName].path, fileType: uploads[docName].fileType,
    }));

    return { uploads, start, retry, remove, reset, summary, completedDocs };
}
